import { useEffect, useMemo, useState } from "react";
import {
  Map, Plus, X, ChevronRight, CheckCircle2, Circle, GripVertical,
  ArrowRight, Trash2, Save, Eye, PlayCircle, History, ChevronDown, ChevronUp,
  SkipForward,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";

/**
 * ClinicianRoadmapPanel — build / edit / assign / approve phase transitions
 * for a patient's rehabilitation roadmap.
 *
 * Every mutation returns the fresh roadmap doc (including the new version)
 * so we always send an up-to-date `version` on the next request, protecting
 * against stale concurrent edits from another clinician.
 */
export default function ClinicianRoadmapPanel({ patientId, programme }) {
  const [state, setState] = useState({
    loading: true, error: null,
    active: null, drafts: [], archived: [], history: [],
  });
  const [builderRoadmap, setBuilderRoadmap] = useState(null); // draft OR active in edit mode
  const [creating, setCreating] = useState(false);
  const [transition, setTransition] = useState(null); // { toPhaseId, action, reason, override }
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/roadmaps/patient/${patientId}`);
      setState({
        loading: false, error: null,
        active: data.active || null,
        drafts: data.drafts || [],
        archived: data.archived || [],
        history: data.history || [],
      });
    } catch (e) {
      setState((s) => ({
        ...s, loading: false,
        error: e?.response?.data?.detail || "Could not load roadmaps.",
      }));
    }
  };

  useEffect(() => { load(); }, [patientId]);

  // -----------------------------------------------------------------------
  const createDraft = async () => {
    setCreating(true);
    try {
      const { data: tpl } = await api.get("/roadmaps/template");
      const { data: draft } = await api.post("/roadmaps", {
        patient_id: patientId,
        programme_id: programme?.id || null,
        title: "Recovery roadmap",
        phases: tpl.phases.map(({ id, ...p }) => p),  // let backend re-issue ids
      });
      toast.success("Draft roadmap created from template");
      setBuilderRoadmap(draft);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create draft");
    } finally { setCreating(false); }
  };

  const openEdit = (roadmap) => setBuilderRoadmap(JSON.parse(JSON.stringify(roadmap)));

  // -----------------------------------------------------------------------
  return (
    <div className="fl-card fl-card-elevated p-5 mt-4" data-testid="clinician-roadmap-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Map className="text-[#f47a20]" size={16} />
          <div className="fl-heading text-xl">Recovery roadmap</div>
        </div>
        <div className="flex items-center gap-2">
          <button className="fl-btn-ghost text-xs" onClick={() => setShowHistory((v) => !v)}
                  data-testid="roadmap-history-toggle">
            <History size={12} /> {showHistory ? "Hide" : "Show"} history ({state.history.length})
          </button>
          {!state.active && !state.drafts.length && (
            <button className="fl-btn text-xs" onClick={createDraft} disabled={creating}
                    data-testid="roadmap-create-btn">
              <Plus size={12} /> {creating ? "Creating…" : "Create from template"}
            </button>
          )}
        </div>
      </div>

      {state.loading && <Skeleton className="h-24 rounded" data-testid="roadmap-loading" />}
      {state.error && (
        <div className="text-sm text-red-300" data-testid="roadmap-error">
          {state.error} <button onClick={load} className="fl-btn-ghost text-xs ml-2">Retry</button>
        </div>
      )}

      {!state.loading && !state.error && (
        <div className="space-y-4">
          {/* Active */}
          {state.active ? (
            <ActiveRoadmapView
              roadmap={state.active}
              history={state.history}
              onEdit={() => openEdit(state.active)}
              onTransition={(payload) => setTransition(payload)}
              onCriterion={async (phaseId, criterionId, confirmed) => {
                try {
                  await api.post(`/roadmaps/${state.active.id}/criteria/${criterionId}/confirm`, {
                    confirmed, version: state.active.version,
                  });
                  toast.success(confirmed ? "Criterion confirmed" : "Criterion unconfirmed");
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed");
                }
              }}
            />
          ) : (
            <div className="text-sm text-zinc-500" data-testid="roadmap-no-active">
              No active roadmap for this patient.
            </div>
          )}

          {/* Drafts */}
          {state.drafts.length > 0 && (
            <div>
              <div className="fl-label mb-2">Drafts</div>
              <div className="space-y-2">
                {state.drafts.map((d) => (
                  <DraftRow key={d.id} draft={d} onEdit={() => openEdit(d)}
                            onAssigned={load} />
                ))}
              </div>
            </div>
          )}

          {/* Archived */}
          {state.archived.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-zinc-400 hover:text-white"
                       data-testid="roadmap-archived-toggle">
                Archived ({state.archived.length})
              </summary>
              <div className="mt-2 space-y-1 text-xs text-zinc-500">
                {state.archived.map((a) => (
                  <div key={a.id} className="border-l-2 border-white/10 pl-3">
                    <div className="text-zinc-300">{a.title}</div>
                    <div>Archived · {a.programme_id ? `linked to programme ${String(a.programme_id).slice(0, 8)}…` : "no programme"}</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {showHistory && (
        <div className="mt-4 border-t border-white/5 pt-3" data-testid="roadmap-history-list">
          <div className="fl-label mb-2">Transition history</div>
          {state.history.length === 0 ? (
            <div className="text-xs text-zinc-500">No transitions yet.</div>
          ) : (
            <ul className="space-y-1.5 text-xs max-h-64 overflow-auto pr-1">
              {state.history.map((h) => (
                <li key={h.id} className="flex items-start gap-2 border-l-2 border-[#f47a20]/40 pl-3">
                  <div className="flex-1">
                    <div className="text-zinc-200">
                      {h.action === "assignment" ? "Assigned" : h.action}
                      {h.from_phase_title ? ` · ${h.from_phase_title} → ` : " · "}
                      <span className="text-[#f47a20]">{h.to_phase_title}</span>
                      {h.override ? <span className="ml-2 text-amber-400">(override)</span> : null}
                    </div>
                    <div className="text-zinc-500">
                      {new Date(h.created_at).toLocaleString("en-GB")} · {h.clinician_name}
                    </div>
                    {h.reason && <div className="text-zinc-400 mt-0.5">"{h.reason}"</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {builderRoadmap && (
        <RoadmapBuilder
          roadmap={builderRoadmap}
          onClose={() => setBuilderRoadmap(null)}
          onSaved={() => { setBuilderRoadmap(null); load(); }}
        />
      )}

      {transition && state.active && (
        <TransitionDialog
          roadmap={state.active}
          initial={transition}
          onClose={() => setTransition(null)}
          onDone={() => { setTransition(null); load(); }}
        />
      )}
    </div>
  );
}

/* ==================================================================== */
function ActiveRoadmapView({ roadmap, history, onEdit, onTransition, onCriterion }) {
  const phases = useMemo(
    () => [...(roadmap.phases || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [roadmap],
  );
  const currentIdx = phases.findIndex((p) => p.id === roadmap.current_phase_id);
  const current = currentIdx >= 0 ? phases[currentIdx] : null;
  const reachedIds = useMemo(() => {
    const s = new Set();
    (history || []).forEach((h) => { if (h.to_phase_id) s.add(h.to_phase_id); });
    return s;
  }, [history]);
  const statusOf = (i) => {
    if (currentIdx < 0) return "upcoming";
    if (i === currentIdx) return "current";
    if (i > currentIdx) return "upcoming";
    return reachedIds.has(phases[i].id) ? "completed" : "skipped";
  };

  return (
    <div data-testid="active-roadmap">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="fl-heading text-lg">{roadmap.title}</div>
          <div className="text-xs text-zinc-400">
            {current ? `Current · Phase ${currentIdx + 1} of ${phases.length} — ${current.title}` : "No current phase"}
          </div>
        </div>
        <button className="fl-btn-ghost text-xs" onClick={onEdit} data-testid="roadmap-edit-active">
          Edit
        </button>
      </div>

      <ol className="mt-3 grid gap-2 md:grid-cols-3 lg:grid-cols-6">
        {phases.map((p, i) => {
          const st = statusOf(i);
          const Icon = st === "completed" ? CheckCircle2 :
                       st === "current" ? PlayCircle :
                       st === "skipped" ? SkipForward : Circle;
          const color = st === "completed" ? "text-emerald-400" :
                        st === "current" ? "text-[#f47a20]" :
                        st === "skipped" ? "text-amber-400" : "text-zinc-500";
          const border = st === "current" ? "border-[#f47a20]/60" :
                         st === "completed" ? "border-emerald-500/40" :
                         st === "skipped" ? "border-amber-500/40 border-dashed" : "border-white/10";
          return (
            <li key={p.id}
                className={`fl-card p-3 border ${border}`}
                data-testid={`clin-roadmap-phase-${i}`}>
              <div className={`flex items-center gap-2 ${color}`}>
                <Icon size={14} />
                <span className="text-[10px] uppercase tracking-widest">
                  {st === "completed" ? "Completed" :
                   st === "current" ? "Current" :
                   st === "skipped" ? "Skipped" : "Upcoming"}
                </span>
              </div>
              <div className="fl-heading text-sm mt-1 truncate">{p.title}</div>
              <div className="text-[10px] text-zinc-500 mt-1">Phase {i + 1} of {phases.length}</div>
            </li>
          );
        })}
      </ol>

      {current && (
        <div className="mt-4 border-t border-white/5 pt-3">
          <div className="fl-label mb-1">Current phase — criteria</div>
          {(current.criteria || []).length === 0 ? (
            <div className="text-xs text-zinc-500">No criteria recorded for this phase.</div>
          ) : (
            <ul className="space-y-1.5">
              {current.criteria.map((c) => (
                <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`clin-crit-${c.id}`}>
                  <button
                    type="button"
                    onClick={() => onCriterion(current.id, c.id, !c.confirmed)}
                    aria-label={c.confirmed ? "Unconfirm criterion" : "Confirm criterion"}
                    data-testid={`clin-crit-toggle-${c.id}`}
                    className="mt-0.5"
                  >
                    {c.confirmed
                      ? <CheckCircle2 size={16} className="text-emerald-400" />
                      : <Circle size={16} className="text-zinc-500 hover:text-zinc-300" />}
                  </button>
                  <div className="flex-1">
                    <div className="text-zinc-200">{c.label}</div>
                    <div className={`text-[11px] ${c.confirmed ? "text-emerald-400" : "text-zinc-500"}`}>
                      {c.confirmed ? "Confirmed" : "Not yet confirmed"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            {currentIdx + 1 < phases.length && (
              <button
                className="fl-btn text-xs"
                onClick={() => onTransition({
                  toPhaseId: phases[currentIdx + 1].id, action: "advance",
                })}
                data-testid="roadmap-advance-btn"
              >
                <ArrowRight size={12} /> Advance to "{phases[currentIdx + 1].title}"
              </button>
            )}
            {currentIdx > 0 && (
              <button
                className="fl-btn-ghost text-xs"
                onClick={() => onTransition({
                  toPhaseId: phases[currentIdx - 1].id, action: "regress",
                })}
                data-testid="roadmap-regress-btn"
              >
                Return to "{phases[currentIdx - 1].title}"
              </button>
            )}
            {currentIdx + 2 < phases.length && (
              <button
                className="fl-btn-ghost text-xs"
                onClick={() => onTransition({
                  toPhaseId: phases[currentIdx + 2].id, action: "skip",
                })}
                data-testid="roadmap-skip-btn"
              >
                Skip to "{phases[currentIdx + 2].title}"
              </button>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">
            Advancing, returning or skipping opens a confirmation dialog. Exercise programmes
            are not changed by roadmap transitions.
          </p>
        </div>
      )}
    </div>
  );
}

/* ==================================================================== */
function DraftRow({ draft, onEdit, onAssigned }) {
  const [starting, setStarting] = useState(draft.phases?.[0]?.id || "");
  const [reason, setReason] = useState("Roadmap assigned");
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    if (!starting) return toast.error("Choose a starting phase");
    setBusy(true);
    try {
      await api.post(`/roadmaps/${draft.id}/assign`, {
        starting_phase_id: starting, version: draft.version, reason,
      });
      toast.success("Roadmap assigned");
      onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fl-card p-3 border border-white/10" data-testid={`draft-${draft.id}`}>
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <div>
          <div className="fl-heading text-sm">{draft.title}</div>
          <div className="text-xs text-zinc-500">
            {(draft.phases || []).length} phases · draft
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="fl-btn-ghost text-xs" onClick={onEdit} data-testid={`draft-edit-${draft.id}`}>
            <Eye size={12} /> Edit / preview
          </button>
        </div>
      </div>

      <div className="mt-3 grid md:grid-cols-3 gap-2 items-end">
        <label className="flex flex-col text-xs text-zinc-400">
          Starting phase
          <select
            className="fl-input mt-1" value={starting}
            onChange={(e) => setStarting(e.target.value)}
            data-testid={`draft-starting-${draft.id}`}
          >
            {(draft.phases || []).map((p, i) => (
              <option key={p.id} value={p.id}>{i + 1}. {p.title}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-zinc-400 md:col-span-2">
          Assignment note
          <input className="fl-input mt-1" value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 data-testid={`draft-reason-${draft.id}`} />
        </label>
      </div>

      <button className="fl-btn text-xs mt-3" onClick={assign} disabled={busy}
              data-testid={`draft-assign-${draft.id}`}>
        <PlayCircle size={12} /> {busy ? "Assigning…" : "Assign this roadmap"}
      </button>
    </div>
  );
}

/* ==================================================================== */
function RoadmapBuilder({ roadmap, onClose, onSaved }) {
  const [phases, setPhases] = useState(roadmap.phases || []);
  const [title, setTitle] = useState(roadmap.title || "Recovery roadmap");
  const [saving, setSaving] = useState(false);
  const isActive = roadmap.status === "active";

  const addPhase = () => setPhases([...phases, { id: null, title: "New phase", description: "", goals: [], criteria: [] }]);
  const removePhase = (idx) => {
    if (isActive && phases[idx].id === roadmap.current_phase_id) {
      return toast.error("Move to another phase before removing the current one");
    }
    setPhases(phases.filter((_, i) => i !== idx));
  };
  const move = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= phases.length) return;
    const cp = [...phases];
    [cp[idx], cp[next]] = [cp[next], cp[idx]];
    setPhases(cp);
  };
  const patchPhase = (idx, patch) => {
    setPhases(phases.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const addCriterion = (idx) => patchPhase(idx, {
    criteria: [...(phases[idx].criteria || []), { id: null, label: "" }],
  });
  const patchCriterion = (idx, cIdx, patch) => {
    const cur = phases[idx].criteria || [];
    patchPhase(idx, { criteria: cur.map((c, j) => j === cIdx ? { ...c, ...patch } : c) });
  };
  const removeCriterion = (idx, cIdx) => {
    patchPhase(idx, { criteria: (phases[idx].criteria || []).filter((_, j) => j !== cIdx) });
  };
  const addGoal = (idx) => patchPhase(idx, { goals: [...(phases[idx].goals || []), ""] });
  const patchGoal = (idx, gIdx, val) => {
    patchPhase(idx, { goals: (phases[idx].goals || []).map((g, j) => j === gIdx ? val : g) });
  };
  const removeGoal = (idx, gIdx) => {
    patchPhase(idx, { goals: (phases[idx].goals || []).filter((_, j) => j !== gIdx) });
  };

  const save = async () => {
    if (!phases.length) return toast.error("A roadmap needs at least one phase");
    if (phases.some((p) => !(p.title || "").trim())) return toast.error("Every phase needs a title");
    setSaving(true);
    try {
      await api.put(`/roadmaps/${roadmap.id}`, {
        title, phases, version: roadmap.version,
      });
      toast.success("Roadmap saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center p-3 sm:p-6"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="roadmap-builder"
    >
      <div
        role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="fl-card-elevated w-full max-w-3xl overflow-hidden max-h-[92vh] flex flex-col"
        style={{ background: "var(--fl-bg-2)" }}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div className="min-w-0 flex-1">
            <div className="fl-label text-[#f47a20]">
              {isActive ? "Edit active roadmap" : "Edit draft"}
            </div>
            <input className="fl-input mt-1 text-lg" value={title}
                   onChange={(e) => setTitle(e.target.value)}
                   data-testid="builder-title" />
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="builder-close" className="text-zinc-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {phases.map((p, idx) => (
            <div key={p.id || `n-${idx}`} className="fl-card p-3 border border-white/10"
                 data-testid={`builder-phase-${idx}`}>
              <div className="flex items-center gap-2">
                <GripVertical size={14} className="text-zinc-500" />
                <input className="fl-input flex-1" value={p.title}
                       onChange={(e) => patchPhase(idx, { title: e.target.value })}
                       data-testid={`builder-phase-title-${idx}`} />
                <button className="fl-btn-ghost text-[11px]" onClick={() => move(idx, -1)}
                        disabled={idx === 0} data-testid={`builder-move-up-${idx}`}>
                  <ChevronUp size={12} />
                </button>
                <button className="fl-btn-ghost text-[11px]" onClick={() => move(idx, 1)}
                        disabled={idx === phases.length - 1} data-testid={`builder-move-down-${idx}`}>
                  <ChevronDown size={12} />
                </button>
                <button className="fl-btn-ghost text-[11px] text-red-300"
                        onClick={() => removePhase(idx)}
                        data-testid={`builder-remove-${idx}`}>
                  <Trash2 size={12} />
                </button>
              </div>
              <textarea className="fl-input mt-2" rows={2}
                        placeholder="Patient-friendly description"
                        value={p.description || ""}
                        onChange={(e) => patchPhase(idx, { description: e.target.value })}
                        data-testid={`builder-phase-desc-${idx}`} />

              <div className="mt-3">
                <div className="flex items-center justify-between">
                  <div className="fl-label text-xs">Goals</div>
                  <button className="fl-btn-ghost text-[11px]" onClick={() => addGoal(idx)}
                          data-testid={`builder-add-goal-${idx}`}>
                    <Plus size={10} /> Goal
                  </button>
                </div>
                {(p.goals || []).map((g, gi) => (
                  <div key={gi} className="flex items-center gap-2 mt-1">
                    <input className="fl-input flex-1 text-sm" value={g}
                           onChange={(e) => patchGoal(idx, gi, e.target.value)}
                           data-testid={`builder-goal-${idx}-${gi}`} />
                    <button className="text-zinc-500 hover:text-red-300"
                            onClick={() => removeGoal(idx, gi)}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between">
                  <div className="fl-label text-xs">Progression criteria</div>
                  <button className="fl-btn-ghost text-[11px]" onClick={() => addCriterion(idx)}
                          data-testid={`builder-add-criterion-${idx}`}>
                    <Plus size={10} /> Criterion
                  </button>
                </div>
                {(p.criteria || []).map((c, ci) => (
                  <div key={c.id || `n-${ci}`} className="flex items-center gap-2 mt-1">
                    <Circle size={12} className="text-zinc-500 shrink-0" />
                    <input className="fl-input flex-1 text-sm"
                           placeholder="e.g. Pain ≤ 3/10 for 3 consecutive days"
                           value={c.label}
                           onChange={(e) => patchCriterion(idx, ci, { label: e.target.value })}
                           data-testid={`builder-criterion-${idx}-${ci}`} />
                    <button className="text-zinc-500 hover:text-red-300"
                            onClick={() => removeCriterion(idx, ci)}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button className="fl-btn-ghost text-xs" onClick={addPhase} data-testid="builder-add-phase">
            <Plus size={12} /> Add phase
          </button>
        </div>

        <div className="border-t border-white/10 p-3 flex items-center justify-between gap-2">
          <div className="text-[11px] text-zinc-500">
            {isActive ? "Editing an active roadmap will not move the current phase." : "Draft — assign later to activate."}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="fl-btn-ghost text-xs" data-testid="builder-cancel">Cancel</button>
            <button onClick={save} className="fl-btn text-xs" disabled={saving} data-testid="builder-save">
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
function TransitionDialog({ roadmap, initial, onClose, onDone }) {
  const phases = useMemo(
    () => [...(roadmap.phases || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [roadmap],
  );
  const current = phases.find((p) => p.id === roadmap.current_phase_id);
  const to = phases.find((p) => p.id === initial.toPhaseId);

  const unconfirmed = (current?.criteria || []).filter((c) => !c.confirmed);
  const overrideNeeded = initial.action === "advance"
    && (!(current?.criteria || []).length || unconfirmed.length > 0);

  const [reason, setReason] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    if (overrideNeeded && !override) return toast.error("Override required to advance");
    setBusy(true);
    try {
      await api.post(`/roadmaps/${roadmap.id}/transition`, {
        to_phase_id: initial.toPhaseId,
        action: initial.action, reason, override,
        version: roadmap.version,
      });
      toast.success(`Phase ${initial.action} recorded`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transition failed");
    } finally { setBusy(false); }
  };

  const actionLabel = { advance: "Advance", regress: "Return", skip: "Skip" }[initial.action] || initial.action;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-start sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="roadmap-transition-dialog"
    >
      <div role="dialog" aria-modal="true"
           onClick={(e) => e.stopPropagation()}
           className="fl-card-elevated w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
           style={{ background: "var(--fl-bg-2)" }}>
        <div className="flex items-center justify-between gap-3 p-4 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div>
            <div className="fl-label text-[#f47a20]">{actionLabel} phase</div>
            <div className="fl-heading text-lg mt-1">
              {current?.title || "—"} <ChevronRight size={14} className="inline text-zinc-500" /> {to?.title || "—"}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white"
                  data-testid="transition-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto text-sm">
          <div>
            <div className="fl-label mb-1">Current criteria</div>
            {(current?.criteria || []).length === 0 ? (
              <div className="text-xs text-zinc-500">
                No criteria recorded — an empty list is not treated as "all met".
              </div>
            ) : (
              <ul className="space-y-1">
                {(current.criteria).map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-xs">
                    {c.confirmed
                      ? <CheckCircle2 size={12} className="text-emerald-400" />
                      : <Circle size={12} className="text-zinc-500" />}
                    <span className={c.confirmed ? "text-zinc-200" : "text-zinc-400"}>
                      {c.label}
                    </span>
                    <span className={c.confirmed ? "text-emerald-400 ml-auto" : "text-zinc-500 ml-auto"}>
                      {c.confirmed ? "confirmed" : "not confirmed"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {overrideNeeded && (
            <label className="flex items-start gap-2 text-xs text-amber-300"
                   data-testid="transition-override-label">
              <input type="checkbox" className="mt-0.5" checked={override}
                     onChange={(e) => setOverride(e.target.checked)}
                     data-testid="transition-override" />
              <span>
                Not all criteria are confirmed — tick to override and record this
                as a clinician decision.
              </span>
            </label>
          )}

          <label className="block text-xs text-zinc-400">
            Reason / comment (required)
            <textarea rows={3} className="fl-input mt-1" value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="transition-reason" />
          </label>

          <div className="text-[11px] text-zinc-500">
            The exercise programme will not be changed by this transition.
          </div>
        </div>

        <div className="border-t border-white/10 p-3 flex items-center justify-end gap-2">
          <button className="fl-btn-ghost text-xs" onClick={onClose} data-testid="transition-cancel">Cancel</button>
          <button className="fl-btn text-xs" onClick={submit} disabled={busy}
                  data-testid="transition-confirm">
            {busy ? "Saving…" : `Confirm ${actionLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
